import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { Editor } from "tinymce";
import {
  SSMLTag,
  TTSCustomEditorColors,
  underlineStyleTags,
} from "shared/constants";
import { titleCase } from "./tts-custom-editor";
import { RefObject, useEffect, useState } from "react";
import {
  getCurrentSSMLValue,
  updateSSMLTag,
} from "features/TTS/TTSCustomEditor/utils/editorKeyDownHandler";
import { theme } from "@web-applications/allinga-web-components";

interface SSMLContentConfig {
  tagType: string;
  tagValue: string;
  tagColor: string;
  uid: string;
  content: string;
  leadingSpace?: string;
  trailingSpace?: string;
}

function prepareSelectionForWrapApply(editor: Editor, menuTagType: SSMLTag) {
  const sel = editor.selection;
  const rng = sel.getRng();

  const selectedText = sel.getContent({ format: "text" }) || "";
  const selectedHTML = sel.getContent({ format: "html" }) || "";

  if (selectedHTML.includes("data-ssml")) return;

  const startEl = getParentElement(rng.startContainer);
  const endEl = getParentElement(rng.endContainer);
  if (!startEl || !endEl) return;

  const startSSML = startEl.closest?.("span[data-ssml]") as HTMLElement | null;
  const endSSML = endEl.closest?.("span[data-ssml]") as HTMLElement | null;
  if (!startSSML || !endSSML) return;

  if (startSSML !== endSSML) return;

  const wrapper = startSSML;
  const data = wrapper.getAttribute("data-ssml") || "";
  const wrapperType = (data.split("-")[0] || "") as SSMLTag;

  if (
    (menuTagType === SSMLTag.SayAs || menuTagType === SSMLTag.Phoneme) &&
    (wrapperType === SSMLTag.Voice || wrapperType === SSMLTag.Style)
  ) {
    return;
  }

  const wrapperText = (wrapper.textContent || "").trim();
  const selectionIsWholeWrapper =
    selectedText.trim().length > 0 &&
    wrapperText.length > 0 &&
    selectedText.trim() === wrapperText.trim();

  if (!selectionIsWholeWrapper) {
    return;
  }

  const newRng = editor.dom.createRng();
  newRng.setStartBefore(wrapper);
  newRng.setEndAfter(wrapper);
  sel.setRng(newRng);
}

function generateSSMLContent(config: SSMLContentConfig): string {
  const {
    tagType,
    tagValue,
    tagColor,
    uid,
    content,
    leadingSpace = "",
    trailingSpace = "",
  } = config;
  const dataSSMLValue = `${tagType}-${uid}=${tagValue}`;

  const phonemeLabelStyle = `color:${tagColor}; cursor: default;`;
  const nonEditableClass = "mceNonEditable";

  // Voice/style label spans: only-styling only (no mceNonEditable, no contenteditable="false").
  const voiceStyleLabelStyle = `color:${tagColor};`;

  if (underlineStyleTags.includes(tagType as SSMLTag)) {
    return `${leadingSpace}<span style="border-bottom: 1px solid; display:inline-block; border-bottom-color: ${tagColor}; padding-bottom: 2px;" data-ssml='${dataSSMLValue}'>${content}</span>${trailingSpace}`;
  }

  if (tagType === SSMLTag.Emphasis) {
    return `${leadingSpace}<span style="font-weight: 600; font-size: 16px; color: ${tagColor};" data-ssml='${dataSSMLValue}'>${content}</span>${trailingSpace}`;
  }

  if (tagType === SSMLTag.Phoneme) {
    return `${leadingSpace}<span data-ssml='${dataSSMLValue}' style="display: inline-block;"><span only-styling contenteditable="false" class="${nonEditableClass}" style="${phonemeLabelStyle}">{</span>${content}<span only-styling contenteditable="false" class="${nonEditableClass}" style="${phonemeLabelStyle}">}</span></span>${trailingSpace}`;
  }

  if (tagType === SSMLTag.Voice) {
    const voiceName = titleCase(tagValue.split(", ")[0]);
    return `${leadingSpace}<span data-ssml='${dataSSMLValue}' style="display: inline-block;"><span only-styling style="${voiceStyleLabelStyle}">[${voiceName}: </span>${content}<span only-styling style="${voiceStyleLabelStyle}">]</span></span>${trailingSpace}`;
  }

  if (tagType === SSMLTag.Style) {
    const styleName = titleCase(tagValue.split(", ")[0]);
    return `${leadingSpace}<span data-ssml='${dataSSMLValue}' style="display: inline-block;"><span only-styling style="${voiceStyleLabelStyle}">[${styleName}: </span>${content}<span only-styling style="${voiceStyleLabelStyle}">]</span></span>${trailingSpace}`;
  }

  return "";
}

function getParentElement(node: Node): Element | null {
  return node.nodeType === Node.TEXT_NODE
    ? node.parentElement
    : (node as Element);
}

function findSSMLAncestor(node: Node): Element | null {
  let checkNode = getParentElement(node);

  while (
    checkNode &&
    checkNode.nodeName !== "P" &&
    checkNode.nodeName !== "BODY"
  ) {
    if (
      checkNode.hasAttribute("data-ssml") ||
      checkNode.hasAttribute("only-styling")
    ) {
      return checkNode;
    }
    checkNode = checkNode.parentElement;
  }

  return null;
}

function insertNodeAfterElement(newNode: Node, afterElement: Element): void {
  if (afterElement.parentNode) {
    afterElement.parentNode.insertBefore(newNode, afterElement.nextSibling);
  }
}

export function handleSSMLValueClick(
  editorRef: RefObject<EditorRef | null>,
  tagType: string, // The SSML tag we want to add (e.g., "rate")
  tagValue: string, // The value to add to the tag (e.g., "50%")
  tagColor: string, // The color for the tag in the editor
  forbiddenParentTags: string[], // SSML tags that cannot be parents of this tag
  uid: string = Date.now().toString() // Generate a default UID if not provided
) {
  if (
    !tagValue.trim() ||
    (tagType !== SSMLTag.Break && parseFloat(tagValue) <= 0)
  ) {
    return;
  }

  const editor = editorRef.current?.editor;
  if (!editor) return;

  const selection = editor.selection;
  const node = selection.getNode() as Element;

  // Handle break tags
  if (tagType === SSMLTag.Break) {
    handleBreakTag(editorRef, node, tagValue, tagColor, uid);
    return;
  }

  // Check for existing tag of same type
  const existingValue = getCurrentSSMLValue(editorRef, tagType as SSMLTag);
  if (existingValue !== null) {
    // Update the existing tag instead of creating a new one
    updateSSMLTag(editorRef, tagType as SSMLTag, tagValue, tagColor, uid);
    return;
  }

  // Trim whitespace from selection
  const range = selection.getRng();
  const trimmedRange = trimSelectionWhitespace(range);

  if (trimmedRange) {
    selection.setRng(trimmedRange);
  }

  // Special handling for nested tags - checking if we're inside another tag
  // Get the content of the current selection
  const currentSelectedContent = editor.selection.getContent();
  if (!currentSelectedContent) return;

  // Normalize content
  const currentNode = editor.selection.getNode();
  currentNode?.parentNode?.normalize();

  // Check forbidden parent tags
  if (!validateParentTags(editorRef, forbiddenParentTags)) {
    return;
  }

  prepareSelectionForWrapApply(editor, tagType as SSMLTag);
  insertRegularTag(editorRef, tagType, tagValue, tagColor, uid);
}

function handleBreakTag(
  editorRef: RefObject<EditorRef | null>,
  node: Element,
  tagValue: string,
  tagColor: string,
  uid: string
): void {
  if (node.classList?.contains("ssml-break")) {
    const dataSsml = node.getAttribute("data-ssml");
    const existingUid = dataSsml?.match(/^break-([^=]+)/)?.[1] || uid;

    node.setAttribute("data-ssml", `break-${existingUid}=${tagValue}`);
    node.textContent = `[${tagValue}]`;

    const editor = editorRef.current?.editor;
    if (editor && node.nextSibling) {
      editor.selection.setCursorLocation(node.nextSibling, 0);
    }
    return;
  }

  insertBreakTag(editorRef, tagValue, tagColor, uid);
}
const insertBreakTag = (
  editorRef: RefObject<EditorRef | null>,
  tagValue: string,
  tagColor: string,
  uid: string
) => {
  const editor = editorRef.current?.editor;
  if (!editor) return;
  editor.undoManager.transact(() => {
    const range = editor.selection.getRng();
    const breakNode = editor.dom.create(
      "span",
      {
        class: "ssml-break mceNonEditable",
        "data-ssml": `break-${uid}=${tagValue}`,
        contenteditable: "false",
        style: `color: ${tagColor}; cursor: default; background-color: transparent; border: 0;`,
      },
      `[${tagValue}]`
    );

    const needSpaceBefore =
      range.startContainer.nodeType === Node.TEXT_NODE &&
      range.startOffset > 0 &&
      (range.startContainer.textContent || "").charAt(range.startOffset - 1) !==
        " ";

    if (range.collapsed) {
      if (needSpaceBefore) {
        range.insertNode(document.createTextNode(" "));
        range.setStartAfter(range.startContainer);
        range.collapse(true);
      }
      range.insertNode(breakNode);
      insertNodeAfterElement(document.createTextNode(" "), breakNode);
    } else {
      const originalRange = range.cloneRange();
      range.collapse(true);
      if (needSpaceBefore) {
        const spaceNode = document.createTextNode(" ");
        range.insertNode(spaceNode);
        range.setStartAfter(spaceNode);
      }
      range.insertNode(breakNode);
      insertNodeAfterElement(document.createTextNode(" "), breakNode);
      editor.selection.setRng(originalRange);
    }
    editor.selection.setCursorLocation(breakNode, 1);
  });
};

function insertRegularTag(
  editorRef: RefObject<EditorRef | null>,
  tagType: string,
  tagValue: string,
  tagColor: string,
  uid: string
): void {
  const editor = editorRef.current?.editor;
  if (!editor) return;

  const selection = editor.selection;
  if (selection.isCollapsed()) return;

  const range = selection.getRng();
  const selectedHtml = selection.getContent({ format: "html" }) || "";
  const selectedText = selection.getContent({ format: "text" }) || "";
  if (!selectedHtml.trim() && !selectedText.trim()) return;

  const ssmlAncestor = findSSMLAncestor(range.startContainer);
  const bookmark = selection.getBookmark();

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = selectedHtml;

  const blockSelector = "p,div,h1,h2,h3,h4,h5,h6,li,blockquote,td,th";
  const blocks = Array.from(tempDiv.querySelectorAll(blockSelector));
  const hasBlockSelection = blocks.length > 0;

  const hasMultipleBlocks = blocks.length > 1;

  editor.undoManager.transact(() => {
    if (ssmlAncestor) {
      const base = selectedHtml;
      const { content, startsWithSpace, endsWithSpace } = processSpaces(base);

      const newContent = generateSSMLContent({
        tagType,
        tagValue,
        tagColor,
        uid,
        content,
        leadingSpace: startsWithSpace ? " " : "",
        trailingSpace: endsWithSpace ? " " : "",
      });

      selection.setContent(newContent);
      return;
    }

    if (hasMultipleBlocks) {
      const firstBlock = blocks[0];
      const lastBlock = blocks[blocks.length - 1];

      const rng = document.createRange();
      rng.setStartBefore(firstBlock);
      rng.setEndAfter(lastBlock);
      selection.setRng(rng);

      const fullHtml = selection.getContent({ format: "html" }) || "";

      // Strip ONLY paragraph wrappers, keep inner tags/spans
      // Convert </p><p> boundaries into <br /> so lines stay visually separated
      const withoutParas = fullHtml
        .replace(/<p[^>]*>/gi, "")
        .replace(/<\/p>/gi, "<br />")
        .replace(/(<br\s*\/?>\s*)+$/i, ""); // remove trailing <br />

      const newContent = generateSSMLContent({
        tagType,
        tagValue,
        tagColor,
        uid,
        content: withoutParas,
      });

      selection.setContent(newContent);
      selection.moveToBookmark(bookmark);
      return;
    }

    if (hasBlockSelection) {
      blocks.forEach((block, index) => {
        const originalInner = block.innerHTML;
        if (!originalInner || !originalInner.replace(/&nbsp;|\s+/g, "").length)
          return;

        const { content, startsWithSpace, endsWithSpace } =
          processSpaces(originalInner);

        block.innerHTML = generateSSMLContent({
          tagType,
          tagValue,
          tagColor,
          uid: `${uid}-${index}`,
          content,
          leadingSpace: startsWithSpace ? " " : "",
          trailingSpace: endsWithSpace ? " " : "",
        });
      });

      selection.setContent(tempDiv.innerHTML);
      selection.moveToBookmark(bookmark);
      return;
    }

    const base = selectedHtml;
    const { content, startsWithSpace, endsWithSpace } = processSpaces(base);

    const newContent = generateSSMLContent({
      tagType,
      tagValue,
      tagColor,
      uid,
      content,
      leadingSpace: startsWithSpace ? " " : "",
      trailingSpace: endsWithSpace ? " " : "",
    });

    selection.setContent(newContent);
    selection.moveToBookmark(bookmark);
  });

  editor.fire("change");
}

function validateParentTags(
  editorRef: RefObject<EditorRef | null>,
  forbiddenParentTags: string[]
): boolean {
  for (const parentTag of forbiddenParentTags) {
    if (!checkParentTag(editorRef, parentTag)) {
      // eslint-disable-next-line no-console
      console.log(
        `Cannot add tag because '${parentTag}' is found in ancestors.`
      );
      return false;
    }
  }
  return true;
}

function checkParentTag(
  editorRef: RefObject<EditorRef | null>,
  parentTag: string
): boolean {
  const editor = editorRef.current?.editor;
  if (!editor) return false;

  const currentNode = editor.selection.getNode() as Element | null;
  if (!currentNode || currentNode.nodeType !== 1) {
    return false;
  }

  const hasSSMLTag = (node: Element) => {
    const dataSSML = node.getAttribute("data-ssml");
    return dataSSML
      ? new RegExp(`\\b${parentTag}(-[^=]+)?=`, "i").test(dataSSML)
      : false;
  };

  let nodeToCheck: Element | null = currentNode;
  while (nodeToCheck) {
    if (hasSSMLTag(nodeToCheck)) {
      // eslint-disable-next-line no-console
      console.log(
        `${parentTag} tag already exists in the selected element or its ancestors.`
      );
      return false;
    }
    nodeToCheck = nodeToCheck.parentElement;
  }

  const selectedContent = editor.selection.getContent({ format: "html" });

  const tagRegex = new RegExp(
    `data-ssml=['"'][^'"]*\\b${parentTag}(-[^=]+)?=`,
    "i"
  );

  return !tagRegex.test(selectedContent);
}

function trimSelectionWhitespace(range: Range): Range | null {
  const clonedRange = range.cloneRange();
  let startOffset = clonedRange.startOffset;
  const endOffset = clonedRange.endOffset;

  // Trim leading whitespace
  if (clonedRange.startContainer.nodeType === Node.TEXT_NODE) {
    const text = clonedRange.startContainer.textContent || "";
    const textFromOffset = text.substring(startOffset);
    const trimmedText = textFromOffset.replace(/^\s+/, "");
    startOffset += textFromOffset.length - trimmedText.length;
    clonedRange.setStart(clonedRange.startContainer, startOffset);
  }

  // Trim trailing whitespace
  if (clonedRange.endContainer.nodeType === Node.TEXT_NODE) {
    const text = clonedRange.endContainer.textContent || "";
    const textToOffset = text.substring(0, endOffset);
    const trimmedText = textToOffset.replace(/\s+$/, "");
    clonedRange.setEnd(clonedRange.endContainer, trimmedText.length);
  }

  const trimmedContent = clonedRange.toString();
  return trimmedContent && trimmedContent.trim().length > 0
    ? clonedRange
    : null;
}

export function processSpaces(content: string): {
  content: string;
  startsWithSpace: boolean;
  endsWithSpace: boolean;
} {
  let processedContent = content;
  const startsWithSpace = /^(&nbsp;| )/.test(processedContent);
  const endsWithSpace = /(&nbsp;| )$/.test(processedContent);

  processedContent = processedContent
    .replace(/^(&nbsp;| )/, "")
    .replace(/(&nbsp;| )$/, "");

  return { content: processedContent, startsWithSpace, endsWithSpace };
}

export function getCurrentBreakValue(
  editorRef: RefObject<EditorRef | null>
): string | null {
  const editor = editorRef.current?.editor;
  if (!editor) return null;

  const node = editor.selection.getNode() as Element;

  if (node.classList?.contains("ssml-break")) {
    const dataSsml = node.getAttribute("data-ssml");
    return dataSsml?.split("=")[1] || null;
  }

  return null;
}

export function isEditorEmpty(editorRef: RefObject<EditorRef | null>) {
  const editor = editorRef.current?.editor;
  return editor ? editor.getContent({ format: "text" }).trim() === "" : true;
}

export function decodeUmlautEntitiesOnly(input: string) {
  const umlautEntities: Record<string, string> = {
    "&ouml;": "ö",
    "&uuml;": "ü",
    "&auml;": "ä",
    "&szlig;": "ß",
    "&Ouml;": "Ö",
    "&Uuml;": "Ü",
    "&Auml;": "Ä",
  };

  return Object.entries(umlautEntities).reduce(
    (result, [entity, char]) => result.replace(new RegExp(entity, "g"), char),
    input
  );
}

// empty p tags are added when we enter a new line in the editor so we need to remove them before sending the text
export function removeParagraphTags(text: string) {
  return (
    text
      // Replace opening <p> tags (with any attributes) with empty string
      // Ensure we only match <p> tags, not tags starting with 'p'
      .replace(/<p(\s[^>]*)?>|<p>/g, "")
      // Replace closing </p> tags with empty string
      .replace(/<\/p>/g, "")
      // Also remove non-breaking spaces
      .replaceAll("&nbsp;", "")
  );
}

export const getToolbarElementColor = (
  isHtmlView: boolean,
  isEditorEmpty: boolean,
  isHovered: boolean = false,
  isActive: boolean = false,
  isDisabled: boolean = false
): string => {
  if (isHtmlView || isEditorEmpty || isDisabled) {
    return TTSCustomEditorColors.IconDisabledColor;
  }
  if (isHovered || isActive) {
    return "text.primary";
  }
  return `${theme.palette.action.active}`;
};

export const getChevronColor = (
  isHtmlView: boolean,
  isEditorEmpty: boolean,
  isDisabled: boolean = false
): string => {
  if (isHtmlView || isEditorEmpty || isDisabled) {
    return TTSCustomEditorColors.IconDisabledColor;
  }
  return TTSCustomEditorColors.ToolbarIconColor;
};

export const calculateAvailableHeight = (
  containerRef: RefObject<HTMLDivElement>,
  headerRef: RefObject<HTMLDivElement>,
  minHeight: number = 300,
  paddingOffset: number = 30
): number => {
  if (!containerRef.current || !headerRef.current) {
    return minHeight;
  }

  const availableHeight =
    containerRef.current.clientHeight -
    headerRef.current.clientHeight -
    paddingOffset;

  return Math.max(availableHeight, minHeight);
};

export function useEditorSelection(
  editorRef: RefObject<EditorRef | null>
): boolean {
  const [hasSelection, setHasSelection] = useState(false);

  // Function to check if there's a selection in the editor
  const checkSelection = () => {
    const editor = editorRef?.current?.editor;
    if (!editor) {
      setHasSelection(false);
      return;
    }

    const selection = editor.selection;
    const selectedContent = selection?.getContent();
    // Check if there's actual selected content (not just empty or whitespace)
    if (!selectedContent?.trim().length) {
      setHasSelection(false);
      return;
    }

    // Check if the selection is within any SSML tags
    const selectedNode = selection.getNode();
    const isWithinSSMLTag =
      selectedNode.closest('[class*="ssml-"]') !== null ||
      Array.from(selectedNode.classList || []).some((className) =>
        className.startsWith("ssml-")
      );

    setHasSelection(!isWithinSSMLTag);
  };

  useEffect(() => {
    const editor = editorRef?.current?.editor;

    if (!editor) return;

    const events = ["selectionchange", "nodechange", "keyup", "mouseup"];
    events.forEach((event) => editor.on(event, checkSelection));
    checkSelection();

    return () => {
      events.forEach((event) => editor.off(event, checkSelection));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorRef]);

  return hasSelection;
}

export function stripEditorHTML(content: string): string {
  const tagsToRemove = [
    /[""]/g,
    /['']/g,
    /<br\s*\/?>/gi,
    /<\/?p>/gi,
    /<\/?div>/gi,
    /<\/?strong>/gi,
    /<\/?em>/gi,
    /<\/?b>/gi,
    /<\/?i>/gi,
    /<\/?u>/gi,
    /<meta[^>]*>/gi,
    /<\/?html>/gi,
    /<\/?body>/gi,
    /<\/?head>/gi,
  ];

  let result = content;

  // Replace basic tags
  result = result.replace(tagsToRemove[0], '"');
  result = result.replace(tagsToRemove[1], "'");
  for (let i = 2; i < tagsToRemove.length; i++) {
    result = result.replace(tagsToRemove[i], "");
  }

  // Remove spans without data-ssml
  while (
    result !==
    (result = result.replace(/<span(?![^>]*data-ssml)[^>]*>|<\/span>/gi, ""))
  ) {
    // Repeat until no more color spans exist
  }

  // Clean up remaining attributes
  result = result
    .replace(/\s+data-ssml="[^"]*"/gi, "")
    .replace(/\s+style="[^"]*"/gi, "")
    .replace(/<span\s*>|<\/span>/gi, "");

  return result.trim();
}
